import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Pagination,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const API_URL = process.env.REACT_APP_API_URL;

export default function UserListView() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // Default: show all
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchUsers = async (query = '') => {
    try {
      const url = query
        ? `${API_URL}/search-users-view/?q=${encodeURIComponent(query)}`
        : `${API_URL}/users/`;
      const res = await axios.get(url);

      // Sort users by created_at descending
      const sortedUsers = res.data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      setUsers(sortedUsers);
      setPage(1); // Reset to first page on new fetch
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    fetchUsers(value);
  };

  const handleRoleToggle = (event, newRole) => {
    if (newRole !== null) {
      setRoleFilter(newRole);
      setPage(1); // Reset to first page on filter change
    }
  };

  const filteredUsers =
    roleFilter === 'all'
      ? users
      : users.filter((u) => {
          const userRoles = Array.isArray(u.roles) && u.roles.length > 0 ? u.roles : [u.role];
          return userRoles.includes(roleFilter);
        });

  // Pagination logic
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const renderUserCard = (u) => {
    const userRoles = Array.isArray(u.roles) && u.roles.length > 0 ? u.roles : [u.role];

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={u.id}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {u.name || ''} {u.surname || ''}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Email: {u.email}
            </Typography>

            {u.contact && (
              <Typography variant="body2" color="textSecondary">
                Contact: {u.contact}
              </Typography>
            )}

            {u.gender && (
              <Typography variant="body2" color="textSecondary">
                Gender: {u.gender}
              </Typography>
            )}

            {u.status && (
              <Typography variant="body2" color="textSecondary">
                Status: {u.status}
              </Typography>
            )}

            {u.city && (
              <Typography variant="body2" color="textSecondary">
                City: {u.city}
              </Typography>
            )}

            <Box mt={1} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                label={u.online ? 'Online' : 'Offline'}
                color={u.online ? 'success' : 'default'}
                size="small"
              />
              {userRoles.map((r, idx) => (
                <Chip
                  key={idx}
                  label={r}
                  variant="outlined"
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
              ))}
            </Box>

            {u.created_at && (
              <Typography
                variant="caption"
                display="block"
                mt={1}
                color="textSecondary"
              >
                Joined: {new Date(u.created_at).toLocaleString()}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <div>
      <Header1 />
      <Typography
        variant="h3"
        component="h1"
        align="center"
        fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
        sx={{
          backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
          mt: 5,
          letterSpacing: 7,
          WebkitBackgroundClip: 'text',
          color: 'transparent',
        }}
        gutterBottom
      >
        All Users View Dashboard
      </Typography>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <TextField
          label="Search users"
          variant="outlined"
          fullWidth
          value={search}
          onChange={handleSearch}
          sx={{ mb: 3 }}
          size={isXs ? 'small' : 'medium'}
        />

        <Box sx={{ overflowX: 'auto', mb: 4 }}>
          <ToggleButtonGroup
            value={roleFilter}
            exclusive
            onChange={handleRoleToggle}
            aria-label="role toggle"
            size={isXs ? 'small' : 'medium'}
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: 1,
              whiteSpace: 'nowrap',
            }}
          >
            <ToggleButton value="all" aria-label="all">All</ToggleButton>
            <ToggleButton value="user" aria-label="user">User</ToggleButton>
            <ToggleButton value="expert" aria-label="expert">Expert</ToggleButton>
            <ToggleButton value="sales-partner-individual" aria-label="sales-partner-individual">
              Sales Partner Ind.
            </ToggleButton>
            <ToggleButton value="sales-partner-company" aria-label="sales-partner-company">
              Sales Partner Bus.
            </ToggleButton>
            <ToggleButton value="ambassador" aria-label="ambassador">
              Campus Ambassador
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Typography
          variant="subtitle1"
          color="textSecondary"
          mb={2}
          sx={{ fontWeight: 'bold' }}
        >
          Showing {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
        </Typography>

        {filteredUsers.length === 0 ? (
          <Typography color="textSecondary">
            No users found for selected role.
          </Typography>
        ) : (
          <>
            {/* Users List using Box */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2, // spacing between items
                justifyContent: 'center',
              }}
            >
              {paginatedUsers.map((user, index) => (
                <Box
                  key={user.id || index}
                  sx={{
                    width: { xs: '100%', sm: '48%', md: '31%' }, // responsive widths
                  }}
                >
                  {renderUserCard(user)}
                </Box>
              ))}
            </Box>

            <Box mt={4} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isXs ? 'small' : 'medium'}
              />
            </Box>
          </>
        )}
      </Container>

      <Footer2 />
    </div>
  );
}
